import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { usePageMeta } from '../hooks/usePageMeta';
import { Section } from '../components/ui/Section';
import { CTASection } from '../components/sections/CTASection';
import { Target, Eye, Compass, TrendingUp, CheckCircle2, Quote } from 'lucide-react';
import { useClients } from '../hooks/useClients';
function AnimatedCounter({
  end,
  suffix = '',
  duration = 2000
}: {
  end: number;
  suffix?: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!isInView) return;
    let startTime: number | null = null;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);
  return <span ref={ref} data-id="element-496">
      {count}
      {suffix}
    </span>;
}
function getYearsOfService(): number {
  const start = new Date(2023, 5, 1); // June 2023
  const now = new Date();
  const diff = now.getTime() - start.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
}
function DecorativeArtwork() {
  return <motion.svg viewBox="0 0 400 400" className="w-full h-full max-h-[400px]" initial={{
    opacity: 0,
    scale: 0.95
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.8
  }} data-id="element-497">
      {/* Concentric circles */}
      <circle cx="200" cy="200" r="160" fill="none" stroke="#00316B" strokeWidth="1" opacity="0.1" data-id="element-498" />
      <circle cx="200" cy="200" r="120" fill="none" stroke="#00316B" strokeWidth="0.8" opacity="0.08" data-id="element-499" />
      <circle cx="200" cy="200" r="80" fill="none" stroke="#00316B" strokeWidth="0.6" opacity="0.08" data-id="element-500" />
      <circle cx="200" cy="200" r="40" fill="#00316B" opacity="0.04" data-id="element-501" />

      {/* Cross lines */}
      <line x1="40" y1="200" x2="360" y2="200" stroke="#00316B" strokeWidth="0.5" opacity="0.06" data-id="element-502" />
      <line x1="200" y1="40" x2="200" y2="360" stroke="#00316B" strokeWidth="0.5" opacity="0.06" data-id="element-503" />

      {/* Diagonal lines */}
      <line x1="80" y1="80" x2="320" y2="320" stroke="#00316B" strokeWidth="0.5" opacity="0.05" data-id="element-504" />
      <line x1="320" y1="80" x2="80" y2="320" stroke="#00316B" strokeWidth="0.5" opacity="0.05" data-id="element-505" />

      {/* Arc segments */}
      <path d="M 60 200 A 140 140 0 0 1 200 60" fill="none" stroke="#00316B" strokeWidth="1.5" opacity="0.12" data-id="element-506" />
      <path d="M 340 200 A 140 140 0 0 1 200 340" fill="none" stroke="#00316B" strokeWidth="1.5" opacity="0.12" data-id="element-507" />

      {/* Scattered dots */}
      <circle cx="120" cy="100" r="3" fill="#00316B" opacity="0.15" data-id="element-508" />
      <circle cx="280" cy="300" r="3" fill="#00316B" opacity="0.15" data-id="element-509" />
      <circle cx="300" cy="120" r="2" fill="#00316B" opacity="0.12" data-id="element-510" />
      <circle cx="100" cy="280" r="2" fill="#00316B" opacity="0.12" data-id="element-511" />
      <circle cx="200" cy="200" r="5" fill="#00316B" opacity="0.18" data-id="element-512" />

      {/* Small accent circles */}
      <circle cx="140" cy="140" r="20" fill="none" stroke="#00316B" strokeWidth="0.8" opacity="0.08" data-id="element-513" />
      <circle cx="260" cy="260" r="20" fill="none" stroke="#00316B" strokeWidth="0.8" opacity="0.08" data-id="element-514" />

      {/* Orange accent */}
      <circle cx="260" cy="140" r="8" fill="#FF6600" opacity="0.1" data-id="element-515" />
    </motion.svg>;
}
export function AboutPage() {
  usePageMeta({
    title: 'About Us',
    description: 'Learn about Belo Digital — a creative digital agency helping businesses across Africa embrace the digital age with confidence.'
  });
  const {
    clients
  } = useClients();
  const yearsOfService = getYearsOfService();
  const stats = [{
    value: yearsOfService,
    suffix: '+',
    label: 'Years of Service'
  }, {
    value: clients.length || 10,
    suffix: '+',
    label: 'Clients Served'
  }, {
    value: 5,
    suffix: '',
    label: 'Team Members'
  }, {
    value: 35,
    suffix: '+',
    label: 'Projects Delivered'
  }];
  const values = [{
    icon: Target,
    title: 'Clarity Over Complexity',
    body: 'We believe digital solutions should simplify, not complicate. Clear thinking and intentional design lead to better outcomes.'
  }, {
    icon: Eye,
    title: 'Functionality With Purpose',
    body: 'A visually appealing product must also perform reliably and serve its intended purpose effectively.'
  }, {
    icon: Compass,
    title: 'Partnership & Collaboration',
    body: 'Strong results come from collaboration. We work as partners, not just service providers.'
  }, {
    icon: TrendingUp,
    title: 'Sustainable Digital Growth',
    body: 'We focus on building solutions that support long-term business objectives rather than short-term trends.'
  }];
  const priorities = ['Thoughtful, user-centered design', 'Clean, maintainable technology', 'Structured and scalable systems', 'Consistency and brand clarity', 'Performance and long-term adaptability'];
  return <main className="pt-24" data-id="element-516">
      {/* Hero */}
      <Section background="default" data-id="element-517">
        <div className="max-w-4xl" data-id="element-518">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-[#0C0C0C] mb-8 leading-tight" data-id="element-519">
            About Belo Digital
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-600 leading-relaxed mb-6" data-id="element-520">
            Belo Digital is a digital solutions agency focused on helping
            businesses build meaningful, high-performing digital presence. We
            combine strategy, design, and technology to create solutions that
            are visually refined, functionally strong, and aligned with real
            business objectives.
          </motion.p>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15
        }} className="text-lg text-gray-600 leading-relaxed" data-id="element-521">
            In a rapidly evolving digital landscape, we believe successful
            digital products are not defined by aesthetics alone, but by
            clarity, usability, and measurable impact. Our work is guided by
            this principle - every project is approached with thoughtful design,
            structured thinking, and a strong emphasis on performance.
          </motion.p>
        </div>
      </Section>

      {/* Stats Counter Section */}
      <Section background="white" data-id="element-522">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12" data-id="element-523">
          {stats.map((stat, index) => <motion.div key={stat.label} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="text-center" data-id="element-524">
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-[#00316B] mb-2" data-id="element-525">
                <AnimatedCounter end={stat.value} suffix={stat.suffix} data-id="element-526" />
              </div>
              <p className="text-sm md:text-base text-gray-500 font-medium" data-id="element-527">
                {stat.label}
              </p>
            </motion.div>)}
        </div>
      </Section>

      {/* Who We Are */}
      <Section background="white" data-id="element-528">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-529">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-530">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-[#0C0C0C] mb-6" data-id="element-531">
              Who We Are
            </h2>
            <div className="space-y-5 text-gray-600 leading-relaxed text-lg" data-id="element-532">
              <p data-id="element-533">
                We are a multidisciplinary digital team driven by curiosity,
                precision, and problem-solving. Belo Digital was built on the
                belief that effective digital solutions emerge from the
                intersection of creative thinking and technical expertise.
              </p>
              <p data-id="element-534">
                Rather than applying generic templates, we work closely with
                businesses to understand their goals, challenges, and audiences.
                This allows us to design and build scalable solutions.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="hidden lg:flex items-center justify-center" data-id="element-535">
            <DecorativeArtwork data-id="element-536" />
          </motion.div>
        </div>
      </Section>

      {/* Our Approach */}
      <Section background="default" data-id="element-537">
        <div className="max-w-4xl mx-auto" data-id="element-538">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-bold font-heading text-[#0C0C0C] mb-6" data-id="element-539">
            Our Approach
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="space-y-5 text-gray-600 leading-relaxed text-lg mb-10" data-id="element-540">
            <p data-id="element-541">
              Our approach is rooted in strategy and clarity. We view design and
              development as tools for solving business problems, improving user
              experiences, and enabling growth.
            </p>
            <p data-id="element-542">
              Every engagement begins with understanding - understanding the
              brand, the users, and the desired outcomes. From there, we craft
              solutions that balance creativity with functionality, ensuring
              that what we build delivers tangible value.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.15
        }} data-id="element-543">
            <p className="text-sm font-semibold text-[#00316B] uppercase tracking-wider mb-5" data-id="element-544">
              We prioritize
            </p>
            <ul className="space-y-4" data-id="element-545">
              {priorities.map((item, index) => <motion.li key={item} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2 + index * 0.05
            }} className="flex items-center gap-4 text-lg text-gray-700" data-id="element-546">
                  <CheckCircle2 className="w-5 h-5 text-[#00316B] shrink-0" data-id="element-547" />
                  {item}
                </motion.li>)}
            </ul>
          </motion.div>
        </div>
      </Section>

      {/* What We Value */}
      <Section background="white" data-id="element-548">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-549">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-bold font-heading text-[#0C0C0C]" data-id="element-550">
            What We Value
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-551">
          {values.map((value, index) => <motion.div key={value.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.08
        }} className="bg-[#F8F8F6] p-8 rounded-xl" data-id="element-552">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-5 shadow-sm" data-id="element-553">
                <value.icon className="w-6 h-6 text-[#00316B]" data-id="element-554" />
              </div>
              <h3 className="text-xl font-bold font-heading text-[#0C0C0C] mb-3" data-id="element-555">
                {value.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-556">{value.body}</p>
            </motion.div>)}
        </div>
      </Section>

      {/* What We Do */}
      <Section background="default" data-id="element-557">
        <div className="max-w-3xl mx-auto text-center" data-id="element-558">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-bold font-heading text-[#0C0C0C] mb-6" data-id="element-559">
            What We Do
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-gray-600 leading-relaxed" data-id="element-560">
            Belo Digital designs, develops, and supports digital solutions
            across product design, systems development, branding, and digital
            strategy. Our work helps businesses establish stronger digital
            presence, improve user experiences, and build technology that
            supports growth.
          </motion.p>
        </div>
      </Section>

      {/* Our Vision */}
      <Section background="dark" data-id="element-561">
        <div className="max-w-4xl mx-auto text-center" data-id="element-562">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="mb-8" data-id="element-563">
            <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto" data-id="element-564">
              <Quote className="w-7 h-7 text-white/80" data-id="element-565" />
            </div>
          </motion.div>
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.05
        }} className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-6" data-id="element-566">
            Our Vision
          </motion.p>
          <motion.blockquote initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-2xl md:text-3xl lg:text-4xl font-heading font-medium text-white leading-snug italic" data-id="element-567">
            "To build digital solutions that are meaningful, scalable, and
            capable of creating lasting value for businesses and their
            audiences."
          </motion.blockquote>
        </div>
      </Section>

      <CTASection data-id="element-568" />
    </main>;
}